import { useState, useEffect, memo } from "react";
import { api } from "../lib/api";
import styles from "./LinkPreview.module.css";

interface LinkPreviewData {
  url: string;
  title: string | null;
  description: string | null;
  image: string | null;
  siteName: string | null;
}

const previewCache = new Map<string, LinkPreviewData | null>();

export const LinkPreview = memo(function LinkPreview({ url }: { url: string }) {
  const [preview, setPreview] = useState<LinkPreviewData | null>(previewCache.get(url) ?? null);
  const [loading, setLoading] = useState(!previewCache.has(url));

  useEffect(() => {
    if (previewCache.has(url)) {
      setPreview(previewCache.get(url)!);
      setLoading(false);
      return;
    }

    let cancelled = false;
    api
      .get<{ preview: LinkPreviewData }>(`/link-preview?url=${encodeURIComponent(url)}`)
      .then((data) => {
        if (!cancelled) {
          previewCache.set(url, data.preview);
          setPreview(data.preview);
        }
      })
      .catch(() => {
        previewCache.set(url, null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [url]);

  if (loading || !preview) return null;
  if (!preview.title && !preview.description && !preview.image) return null;

  return (
    <a
      href={preview.url}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.container}
      onClick={(e) => e.stopPropagation()}
    >
      {preview.image && (
        <div className={styles.imageWrapper}>
          <img
            src={preview.image}
            alt={preview.title ?? ""}
            className={styles.image}
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLElement).style.display = "none";
            }}
          />
        </div>
      )}
      <div className={styles.content}>
        {preview.siteName && (
          <span className={styles.siteName}>{preview.siteName}</span>
        )}
        {preview.title && (
          <span className={styles.title}>{preview.title}</span>
        )}
        {preview.description && (
          <span className={styles.description}>{preview.description}</span>
        )}
      </div>
    </a>
  );
});
